/* React Query rond de datalaag van de offertes. De client is de gewone CRM-client
 * (sessie van de ingelogde gebruiker); de datalaag krijgt hem als argument, zodat de
 * toetsen hem kunnen nabootsen. De offerte zelf en haar regels beheert useQuote. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { haalOffertelijst, type LijstClient } from './data/lijst';
import { haalRevisies, legRevisieVast, type VastlegKern } from './data/revisies';
import {
  legBesluitVast, markeerVerstuurd, zetStatusTerug, type Besluit, type OfferteKern, type Verstuurd,
} from './data/acties';
import { bewaarInProjectmap, type BewaarVerzoek, type ProjectmapClient } from './data/projectmap';
import { dupliceerOfferte, haalOfferteProject, verwijderOfferte } from './data/offerte';

/* De gegenereerde types kennen de kolommen en functie van migratie 097 niet. */
const client = supabase as unknown as ProjectmapClient & LijstClient;

export const offerteSleutel = (id: string | undefined) => ['offerte', id] as const;

export function useOffertelijst() {
  return useQuery({ queryKey: ['offertes', 'lijst'], queryFn: () => haalOffertelijst(client), staleTime: 30_000 });
}

export function useRevisies(quoteId: string | undefined) {
  return useQuery({
    queryKey: [...offerteSleutel(quoteId), 'revisies'],
    queryFn: () => haalRevisies(client, quoteId!),
    enabled: Boolean(quoteId),
  });
}

export function useOfferteProject(projectId: string | null | undefined) {
  return useQuery({
    queryKey: ['offerte', 'project', projectId],
    queryFn: () => haalOfferteProject(client, projectId ?? null),
    enabled: projectId !== undefined,
    staleTime: 5 * 60_000,
  });
}

/** Na elke wijziging: de lijst, de revisies, de projectpagina en het dashboard lezen opnieuw. */
function useVerversen(quoteId: string | undefined) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ['offertes'] });
    qc.invalidateQueries({ queryKey: offerteSleutel(quoteId) });
    qc.invalidateQueries({ queryKey: ['projectpagina'] });
    qc.invalidateQueries({ queryKey: ['projectlijst'] });
    qc.invalidateQueries({ queryKey: ['dashboard'] });
  };
}

export type StatusActie =
  | { soort: 'verstuurd'; v: Verstuurd }
  | { soort: 'besluit'; b: Besluit }
  | { soort: 'terug' };

export function useStatusActie(q: OfferteKern | null) {
  const ververs = useVerversen(q?.id);
  return useMutation({
    mutationFn: async (a: StatusActie) => {
      if (!q) throw new Error('Geen offerte.');
      if (a.soort === 'verstuurd') await markeerVerstuurd(client, q, a.v);
      else if (a.soort === 'besluit') await legBesluitVast(client, q, a.b);
      else await zetStatusTerug(client, q);
    },
    onSettled: ververs,
  });
}

export function useLegRevisieVast(quoteId: string | undefined) {
  const ververs = useVerversen(quoteId);
  return useMutation({
    mutationFn: (v: { q: VastlegKern; nummer: number; notitie: string | null }) => legRevisieVast(client, v.q, v.nummer, v.notitie),
    onSettled: ververs,
  });
}

export function useBewaarInProjectmap(quoteId: string | undefined) {
  const ververs = useVerversen(quoteId);
  return useMutation({
    mutationFn: (v: BewaarVerzoek) => bewaarInProjectmap(client, v),
    // Ook bij een fout: een revisie kan al vastgelegd zijn.
    onSettled: ververs,
  });
}

export function useDupliceer() {
  const ververs = useVerversen(undefined);
  return useMutation({ mutationFn: (bron: Record<string, any>) => dupliceerOfferte(client, bron), onSuccess: ververs });
}

export function useVerwijder(quoteId: string | undefined) {
  const ververs = useVerversen(quoteId);
  return useMutation({ mutationFn: () => verwijderOfferte(client, quoteId!), onSuccess: ververs });
}
